import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import DASS21Form from './components/DASS21Form';
import PSQIForm from './components/PSQIForm';
import SRQAForm from './components/SRQAForm';
import logo from './logo.png';
import logoBocconi from './logoBocconi.png';
import logoMur from './LOGO_MUR.png';
import logoProben from './LOGO_PROBEN.png';

const baseUrl = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

const BLUE = '#00388A';

const allTracks = [
  { id: 1, title: "The Cyclic Sigh", duration: "12 min", author: "Daniel Lumera", category: "SOS — Rapid Regulation", file: "/tracks/track1.mp3" },
  { id: 2, title: "The Inner Space", duration: "6 min", author: "Felicia Cigorescu", category: "SOS — Rapid Regulation", file: "/tracks/track2.mp3" },
  { id: 3, title: "Ground Zero", duration: "15 min", author: "Felicia Cigorescu", category: "Sleep & Unwind", file: "/tracks/track3.mp3" },
  { id: 4, title: "Let It Go", duration: "7 min", author: "Emiliano Toso", category: "Sleep & Unwind", file: "/tracks/track4.mp3" },
  { id: 5, title: "The Heart Breath", duration: "10 min", author: "Daniel Lumera", category: "Breath Training", file: "/tracks/track5.mp3" },
  { id: 6, title: "Sync – Breath Training", duration: "8 min", author: "Felicia Cigorescu", category: "Breath Training", file: "/tracks/track6.mp3" },
  { id: 7, title: "Deep Focus", duration: "8 min", author: "Felicia Cigorescu", category: "Academic Focus", file: "/tracks/track7.mp3" },
  { id: 8, title: "The Perfect Sequence 28", duration: "10 min", author: "Daniel Lumera", category: "Academic Focus", file: "/tracks/track8.mp3" },
  { id: 9, title: "The Creative Mind", duration: "10 min", author: "Felicia Cigorescu", category: "Creative & Flow", file: "/tracks/track9.mp3" },
  { id: 10, title: "Hum • Ham • Om Mantra", duration: "12 min", author: "Daniel Lumera", category: "The Art of Feeling", file: "/tracks/track10.mp3" },
  { id: 11, title: "Feel", duration: "8 min", author: "Emiliano Toso", category: "The Art of Feeling", file: "/tracks/track11.mp3" },
  { id: 12, title: "The Well", duration: "10 min", author: "Daniel Lumera", category: "Contemplation & Meaning", file: "/tracks/track12.mp3" },
  { id: 13, title: "Om So Ham Meditation", duration: "12 min", author: "Daniel Lumera", category: "Contemplation & Meaning", file: "/tracks/track13.mp3" },
];

const playlists = [
  {
    id: 'sos',
    name: "SOS — Rapid Regulation",
    description: "For moments of acute stress or emotional overload. Brief breathing and meditation sequences to restore calm within minutes.",
    tip: "Use before an exam, after a stressful event, or whenever you feel overwhelmed. Find a quiet spot, put on headphones, and allow 10–15 minutes.",
    trackIds: [1, 2]
  },
  {
    id: 'sleep',
    name: "Sleep & Unwind",
    description: "Supports the transition from cognitive activation to deep restorative rest. Helps reduce rumination and evening hyperactivation.",
    tip: "Listen in the evening, 20–30 minutes before bed. Dim the lights, lie down comfortably, and let the tracks guide you into sleep.",
    trackIds: [3, 4]
  },
  {
    id: 'focus',
    name: "Breath Training & Academic Focus",
    description: "Strengthens vagal tone, improves heart rate variability and supports sustained attention during study sessions and complex tasks.",
    tip: "Use at the start of a study session or before deep work. Sit upright, close your eyes and follow the breathing guidance for 8–10 minutes.",
    trackIds: [5, 6, 7, 8]
  },
  {
    id: 'creativity',
    name: "Creative & Flow",
    description: "Facilitates entry into flow states through guided imagery and immersive environments. Supports divergent thinking and creative insight.",
    tip: "Best used when you need fresh ideas or creative problem-solving. Allow yourself 10 minutes of uninterrupted listening in a comfortable space.",
    trackIds: [9]
  },
  {
    id: 'feeling',
    name: "The Art of Feeling & Contemplation",
    description: "Integrates music, poetic language and contemplative practices to support emotional processing, meaning-making and deeper self-awareness.",
    tip: "Use when you need to process emotions or reflect. Choose a calm moment — perhaps after classes or during the weekend. Give yourself space to simply listen and feel.",
    trackIds: [10, 11, 12, 13]
  }
];

const weeklyPlans = [
  { week: "Week 1 — Regulation", description: "Stabilize your nervous system and build a foundation of calm.", tracks: [1, 2, 6] },
  { week: "Week 2 — Recovery", description: "Focus on sleep quality and deep restoration.", tracks: [3, 4, 13] },
  { week: "Week 3 — Performance", description: "Sharpen your focus and cognitive efficiency.", tracks: [5, 7, 8] },
  { week: "Week 4 — Creativity & Integration", description: "Open up to creative thinking and emotional integration.", tracks: [9, 10, 11, 12] }
];

const SequentialPlayer = ({ tracks, onBack, userHash }) => {
  const [currentIdx, setCurrentIdx] = useState(0);
  const audioRef = useRef(null);

  const current = tracks[currentIdx];

  const handleEnded = () => {
    if (currentIdx < tracks.length - 1) {
      setCurrentIdx(currentIdx + 1);
    }
  };

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.load();
      audioRef.current.play().catch(() => {});
      // Track play
      if (userHash) {
        fetch(`${baseUrl}/api/track/play`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_hash: userHash, track_id: current.id })
        }).catch(() => {});
      }
    }
  }, [currentIdx]);

  return (
    <div className="player-container" style={playerContainerStyle}>
      <button onClick={onBack} style={backButtonStyle}>← Back</button>
      <div className="now-playing" style={nowPlayingStyle}>
        <span style={nowPlayingLabelStyle}>Now playing</span>
        <h3 className="now-playing-title" style={nowPlayingTitleStyle}>{current.title}</h3>
        <p style={nowPlayingAuthorStyle}>{current.author}{current.duration ? ` • ${current.duration}` : ''}</p>
        <audio
          ref={audioRef}
          controls
          onEnded={handleEnded}
          style={{ width: '100%', marginTop: '20px' }}
          src={current.file}
        />
      </div>
      <div style={trackListStyle}>
        {tracks.map((t, idx) => (
          <div
            key={t.id}
            onClick={() => setCurrentIdx(idx)}
            className="track-list-item"
            style={{
              ...trackListItemStyle,
              backgroundColor: idx === currentIdx ? 'rgba(0, 56, 138, 0.05)' : 'transparent',
              fontWeight: idx === currentIdx ? '600' : '400',
              cursor: 'pointer'
            }}
          >
            <span style={{ color: idx === currentIdx ? BLUE : '#888', marginRight: '12px', fontSize: '13px' }}>
              {idx === currentIdx ? '▶' : `${idx + 1}`}
            </span>
            <span style={{ flex: 1, color: '#333' }}>{t.title}</span>
            <span style={{ color: '#AAA', fontSize: '13px' }}>{t.duration}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

function App() {
  const [step, setStep] = useState('landing');
  const [userHash, setUserHash] = useState(null);
  const [summary, setSummary] = useState({ dass: null, psqi: null, srqa: null });
  const [playerTracks, setPlayerTracks] = useState(null);
  const [playerFrom, setPlayerFrom] = useState('results');
  const [consentChecked, setConsentChecked] = useState(false);
  const [canRetake, setCanRetake] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlHash = params.get('user_hash');
    if (urlHash) {
      setUserHash(urlHash);
      window.history.replaceState({}, '', '/');
      fetch(`${baseUrl}/api/assessment/status/${urlHash}`)
        .then(r => r.json())
        .then(statusData => {
          if (statusData.completed) {
            setCanRetake(statusData.can_retake);
            setDaysRemaining(statusData.days_remaining);
            setStep('results');
          } else {
            setStep('consent');
          }
        })
        .catch(() => setStep('consent'));
    }
  }, []);

const getPersonalizedTracks = () => {
    let ids = [];
    if (summary.dass?.stress !== "Normal" || summary.dass?.anxiety !== "Normal") { ids.push(1, 2, 6); }
    if (summary.psqi?.sleep_issue_level !== "Normal") { ids.push(3, 4, 13); }
    if (summary.srqa?.profile !== "Normal") { ids.push(5, 7, 8); }
    const unique = [...new Set(ids)];
    return unique.map(id => allTracks.find(t => t.id === id)).filter(Boolean);
};

  /*const handleStart = () => {
    setUserHash('LOCAL_TEST_USER');
    window.scrollTo(0, 0);
    setStep('consent');
  };*/

  // MODALITÀ SSO (da attivare in produzione)
  const handleStart = async () => {
    try {
      const res = await fetch(`${baseUrl}/api/auth/login`);
      const data = await res.json();
      window.location.href = data.login_url;
    } catch {
      alert('Something went wrong. Please try again later.');
    }
  };

  const handleDASSComplete = (res) => { setSummary(prev => ({ ...prev, dass: res.results })); window.scrollTo(0, 0); setStep('psqi'); };
  const handlePSQIComplete = (res) => { setSummary(prev => ({ ...prev, psqi: res })); window.scrollTo(0, 0); setStep('srqa'); };
  const handleSRQAComplete = (res) => { setSummary(prev => ({ ...prev, srqa: res })); window.scrollTo(0, 0); setStep('results'); };

  const openPlayer = (tracks, from) => {
    setPlayerTracks(tracks);
    setPlayerFrom(from);
    window.scrollTo(0, 0);
    setStep('player');
  };

  const openLibraryFromTrack = (startIdx) => {
    const reordered = [...allTracks.slice(startIdx), ...allTracks.slice(0, startIdx)];
    openPlayer(reordered, 'results');
  };

  const personalizedTracks = getPersonalizedTracks();

  return (
    <div className="App" style={mainContainerStyle}>

      {/* 1. LANDING PAGE */}
      {step === 'landing' && (
        <div className="landing-wrapper" style={landingWrapperStyle}>
          {/* BOCCONI LOGO TOP-LEFT */}
          <div style={topBarStyle}>
            <img src={logoBocconi} alt="Bocconi University" className="bocconi-logo" style={bocconiLogoStyle} />
          </div>

          <header style={centerFlowStyle}>
            <p className="hero-tagline" style={heroTaglineStyle}>A Meditation Playlist</p>
          </header>

          <section style={centerFlowStyle}>
            <p className="paragraph" style={paragraphStyle}>We are pleased to introduce InsideArts: A Meditation Playlist, a digital wellbeing resource composed of 13 audio tracks designed to support focus, relaxation and overall academic performance.</p>
            <p className="paragraph" style={paragraphStyle}>Developed on solid scientific foundations and informed by interdisciplinary research in neuroscience, psychology and sound-based practices, the playlist has been created by a team of experts in mental wellbeing, cognitive performance, and creative disciplines. Through a short initial questionnaire, you will be able to access a personalized playlist to help you manage stress, enhance concentration and unwind when needed.</p>
            <p className="paragraph" style={paragraphStyle}>Through a short questionnaire, you will access a personalized listening path designed to help regulate stress, enhance concentration and support emotional balance throughout your academic journey. The app can be used at any time, according to your needs.</p>
            <p className="paragraph" style={{...paragraphStyle, fontWeight: '600'}}>We encourage you to explore this opportunity and take a moment for your wellbeing.</p>
            <button className="btn-primary btn-primary-custom" onClick={handleStart} style={btnPrimaryStyle}>Sign in with University credentials</button>
          </section>

          <footer style={footerStyle}>
            <div style={footerContentStyle}>
              <p style={footerCollabStyle}>In collaboration with</p>
              <img src={logo} alt="InsideArts" className="footer-logo" style={footerLogoStyle} />
              <div style={institutionalSectionStyle}>
                <div className="institutional-logos" style={institutionalLogosStyle}>
                  <img src={logoMur} alt="MUR" className="institutional-logo" style={institutionalLogoStyle} />
                  <img src={logoProben} alt="PRO-BEN" className="institutional-logo" style={institutionalLogoStyle} />
                </div>
                <p className="institutional-text" style={institutionalTextStyle}>Progetto finanziato dal Ministero dell'Università e della Ricerca nell'ambito dell'Avviso Pro-BEN 2024 (D.D. n. 1396 del 18 settembre 2024)</p>
              </div>
            </div>
          </footer>
        </div>
      )}

      {/* 2. INFORMED CONSENT */}
      {step === 'consent' && (
        <div style={centerWrapperStyle}>
          <div className="consent-container" style={consentContainerStyle}>
            <h2 className="consent-title" style={consentTitleStyle}>Before we begin</h2>
            <p style={{ fontSize: '16px', color: '#444', lineHeight: '1.7', marginBottom: '20px', textAlign: 'center' }}>
              Please read the{' '}
              <a href="/Informed_Consent__Meditation_Playlist.pdf" target="_blank" rel="noopener noreferrer" style={{ color: BLUE, fontWeight: '600', textDecoration: 'underline' }}>
                Informed Consent Form
              </a>
              {' '}before proceeding.
            </p>
            <label className="consent-checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', textAlign: 'left', cursor: 'pointer', maxWidth: '600px', margin: '0 auto' }}>
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                style={{ marginTop: '4px', width: '18px', height: '18px', accentColor: BLUE }}
              />
              <span className="consent-checkbox-text" style={{ fontSize: '15px', color: '#444', lineHeight: '1.6' }}>
                I confirm that I received the information that precedes, and I declare having read and understood its content. I confirm that I am 18 years of age or older and volunteer to take part in this research.
                <br /><br />
                Taking note that my Data are processed in full compliance with the Law, I freely consent to my Data to be used in the manner and uses described. I also declare having understood my rights and limitations, as well as how to exercise them.
                <br /><br />
                <strong>By proceeding, I give my consent.</strong>
              </span>
            </label>
            <button
              className="btn-primary"
              onClick={() => { window.scrollTo(0, 0); setStep('dass21'); }}
              disabled={!consentChecked}
              style={{
                ...btnPrimaryStyle,
                opacity: consentChecked ? 1 : 0.4,
                cursor: consentChecked ? 'pointer' : 'not-allowed'
              }}
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {/* 3. QUESTIONNAIRES */}
      {(['dass21', 'psqi', 'srqa'].includes(step)) && (
        <div style={centerWrapperStyle}>
           {step === 'dass21' && <DASS21Form userHash={userHash} onComplete={handleDASSComplete} />}
           {step === 'psqi' && <PSQIForm userHash={userHash} onComplete={handlePSQIComplete} />}
           {step === 'srqa' && <SRQAForm userHash={userHash} onComplete={handleSRQAComplete} />}
        </div>
      )}

      {/* 4. RESULTS PAGE */}
      {step === 'results' && (
        <div className="landing-wrapper" style={landingWrapperStyle}>

        {personalizedTracks.length > 0 && (
          <section style={{...centerFlowStyle, marginBottom: '60px'}}>
            <p className="paragraph" style={paragraphStyle}>We recommend listening to the following personalized playlist:</p>
            <div style={personalizedListStyle}>
              {personalizedTracks.map(t => (
                <div key={t.id} style={personalizedTrackStyle} onClick={() => openPlayer(personalizedTracks, 'results')}>
                  <span style={playIconStyle}>▶</span>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <p style={trackNameStyle}>{t.title}</p>
                    <p style={trackMetaStyle}>{t.author}{t.duration ? ` • ${t.duration}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
            <button
              onClick={() => openPlayer(personalizedTracks, 'results')}
              className="btn-primary"
              style={{...btnPrimaryStyle, marginTop: '20px'}}
            >
              Play personalized playlist
            </button>
          </section>
        )}

          <section style={{...centerFlowStyle, marginBottom: '60px', width: '100%'}}>
            <h2 className="section-title-small" style={sectionTitleSmallStyle}>Explore our playlists</h2>
            <div className="playlist-grid" style={playlistGridStyle}>
              {playlists.map(pl => {
                const tracks = pl.trackIds.map(id => allTracks.find(t => t.id === id)).filter(Boolean);
                return (
                  <div key={pl.id} style={playlistCardStyle}>
                    <h3 style={playlistNameStyle}>{pl.name}</h3>
                    <p style={playlistDescStyle}>{pl.description}</p>
                    <p style={playlistTipStyle}>{pl.tip}</p>
                    <div style={{ marginTop: '15px' }}>
                      {tracks.map(t => (
                        <p key={t.id} style={playlistTrackNameStyle}>{t.title}</p>
                      ))}
                    </div>
                    <button onClick={() => openPlayer(tracks, 'results')} style={playlistPlayBtnStyle}>▶ Listen</button>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={{...centerFlowStyle, marginBottom: '60px', width: '100%'}}>
            <h2 className="section-title-small" style={sectionTitleSmallStyle}>Weekly recommendations</h2>
            <div className="protocol-grid" style={protocolGridStyle}>
              {weeklyPlans.map((w, idx) => {
                const tracks = w.tracks.map(id => allTracks.find(t => t.id === id)).filter(Boolean);
                return (
                  <div key={idx} style={weekCardStyle}>
                    <span style={weekTagStyle}>{w.week}</span>
                    <p style={weekGoalStyle}>{w.description}</p>
                    <div style={{ marginTop: '10px' }}>
                      {tracks.map(t => (
                        <p key={t.id} style={playlistTrackNameStyle}>{t.title}</p>
                      ))}
                    </div>
                    <button onClick={() => openPlayer(tracks, 'results')} style={playlistPlayBtnStyle}>▶ Listen</button>
                  </div>
                );
              })}
            </div>
          </section>

          <section style={{...centerFlowStyle, marginBottom: '60px', width: '100%'}}>
            <h2 className="section-title-small" style={sectionTitleSmallStyle}>Full catalog</h2>
            <div style={catalogListStyle}>
              {allTracks.map((t, idx) => (
                <div key={t.id} style={catalogItemStyle} onClick={() => openLibraryFromTrack(idx)}>
                  <span style={playIconSmallStyle}>▶</span>
                  <div style={{ flex: 1, textAlign: 'left' }}>
                    <p style={trackNameStyle}>{t.title}</p>
                    <p style={trackMetaStyle}>{t.category} • {t.author}{t.duration ? ` • ${t.duration}` : ''}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={{...centerFlowStyle, marginBottom: '60px'}}>
            {canRetake ? (
              <button onClick={() => { window.scrollTo(0, 0); setStep('dass21'); }} style={retakeBtnStyle}>
                Retake questionnaires
              </button>
            ) : (
              <p style={{ fontSize: '14px', color: '#888' }}>
                You can retake the questionnaires in {daysRemaining} days
              </p>
            )}
          </section>

          <section style={counselingSectionStyle}>
            <p className="counseling-text" style={counselingTextStyle}>We care about your wellbeing. We encourage you to contact the University Counseling Service.</p>
            <a href="https://www.unibocconi.it/en/current-students/campus-life/counseling-self-empowerment-and-wellbeing/counseling" target="_blank" rel="noopener noreferrer" className="counseling-link" style={counselingLinkStyle}>University Counseling Service</a>
            <p style={counselingSubTextStyle}>Professional support is always available and reaching out is an important step toward taking care of your own wellbeing.</p>
          </section>

        </div>
      )}

      {/* 5. SEQUENTIAL PLAYER */}
      {step === 'player' && playerTracks && (
        <div style={centerWrapperStyle}>
          <SequentialPlayer
            tracks={playerTracks}
            onBack={() => { window.scrollTo(0, 0); setStep(playerFrom); }}
            userHash={userHash}
          />
        </div>
      )}

    </div>
  );
}

// --- STYLES ---
const mainContainerStyle = { width: '100%', minHeight: '100vh', backgroundColor: '#FFFFFF', fontFamily: '"Inter", sans-serif', color: '#2C2C2C', display: 'flex', flexDirection: 'column', alignItems: 'center' };
const landingWrapperStyle = { maxWidth: '1100px', width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 20px 40px 20px', textAlign: 'center' };
const centerFlowStyle = { display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', width: '100%' };

// TOP BAR WITH BOCCONI LOGO
const topBarStyle = { width: '100%', display: 'flex', justifyContent: 'flex-start', padding: '20px 0 0 0', marginLeft: '-20px' };
const bocconiLogoStyle = { height: '45px', width: 'auto', objectFit: 'contain' };

// HERO - compacted, no InsideArts logo
const heroTaglineStyle = { fontSize: '28px', fontFamily: '"Playfair Display", serif', color: '#575757', margin: '30px 0 20px 0', fontWeight: '300', textAlign: 'center' };

const sectionTitleSmallStyle = { fontFamily: '"Playfair Display", serif', fontSize: '28px', color: '#2C2C2C', marginBottom: '30px', textAlign: 'center' };
const paragraphStyle = { fontSize: '16px', lineHeight: '1.6', color: '#444', maxWidth: '800px', margin: '0 auto 0 auto', textAlign: 'justify' };
const btnPrimaryStyle = { backgroundColor: BLUE, color: 'white', padding: '18px 50px', border: 'none', borderRadius: '60px', fontSize: '17px', fontWeight: '600', cursor: 'pointer', letterSpacing: '1px', margin: '20px auto 0 auto' };
const centerWrapperStyle = { width: '100%', display: 'flex', justifyContent: 'center', padding: '60px 20px' };
const consentContainerStyle = { maxWidth: '800px', width: '100%', backgroundColor: '#FFFFFF', padding: '50px 40px', borderRadius: '20px', boxShadow: '0 2px 15px rgba(0,0,0,0.03)', textAlign: 'center' };
const consentTitleStyle = { fontFamily: '"Playfair Display", serif', fontSize: '32px', color: '#2C2C2C', marginBottom: '30px' };
const personalizedListStyle = { maxWidth: '600px', width: '100%', marginTop: '20px' };
const personalizedTrackStyle = { display: 'flex', alignItems: 'center', gap: '15px', padding: '15px 20px', borderRadius: '12px', backgroundColor: '#FAFAFA', marginBottom: '8px', cursor: 'pointer', transition: 'background 0.2s' };
const playIconStyle = { color: BLUE, fontSize: '18px', width: '30px', textAlign: 'center' };
const playIconSmallStyle = { color: BLUE, fontSize: '14px', width: '24px', textAlign: 'center' };
const trackNameStyle = { fontSize: '16px', fontWeight: '500', color: '#333', margin: '0' };
const trackMetaStyle = { fontSize: '13px', color: '#888', margin: '4px 0 0 0' };
const playlistGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '25px', width: '100%' };
const playlistCardStyle = { padding: '30px', backgroundColor: '#FAFAFA', borderRadius: '20px', textAlign: 'left', display: 'flex', flexDirection: 'column' };
const playlistNameStyle = { fontFamily: '"Playfair Display", serif', fontSize: '20px', color: BLUE, marginBottom: '10px' };
const playlistDescStyle = { fontSize: '14px', color: '#444', lineHeight: '1.5', marginBottom: '10px' };
const playlistTipStyle = { fontSize: '13px', color: '#888', lineHeight: '1.5', fontStyle: 'italic', marginBottom: '10px' };
const playlistTrackNameStyle = { fontSize: '14px', color: '#555', margin: '4px 0' };
const playlistPlayBtnStyle = { marginTop: 'auto', paddingTop: '15px', background: 'none', border: `1px solid ${BLUE}`, color: BLUE, padding: '10px 24px', borderRadius: '50px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', alignSelf: 'flex-start', transition: 'all 0.2s' };
const protocolGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', width: '100%' };
const weekCardStyle = { padding: '30px', backgroundColor: '#FAFAFA', borderRadius: '20px', textAlign: 'left' };
const weekTagStyle = { fontSize: '12px', color: BLUE, fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '10px' };
const weekGoalStyle = { fontSize: '14px', color: '#666', lineHeight: '1.4', marginBottom: '10px' };
const catalogListStyle = { maxWidth: '700px', width: '100%' };
const catalogItemStyle = { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderBottom: '1px solid #F5F5F5', cursor: 'pointer', transition: 'background 0.2s' };
const retakeBtnStyle = { background: 'none', border: '1px solid #DDD', color: '#888', padding: '14px 40px', borderRadius: '50px', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s' };
const counselingSectionStyle = { width: '100%', padding: '40px 20px', borderTop: '1px solid #F0F0F0', textAlign: 'center', marginTop: '40px' };
const counselingTextStyle = { fontSize: '17px', color: '#444', lineHeight: '1.6', maxWidth: '700px', margin: '0 auto' };
const counselingLinkStyle = { fontSize: '16px', color: BLUE, fontWeight: '600', textDecoration: 'none', display: 'inline-block', marginTop: '15px' };
const counselingSubTextStyle = { fontSize: '15px', color: '#444', lineHeight: '1.6', maxWidth: '700px', margin: '15px auto 0 auto', textAlign: 'center' };
const playerContainerStyle = { maxWidth: '700px', width: '100%' };
const nowPlayingStyle = { textAlign: 'center', padding: '30px', backgroundColor: '#FAFAFA', borderRadius: '20px', marginBottom: '20px' };
const nowPlayingLabelStyle = { fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', color: '#AAA' };
const nowPlayingTitleStyle = { fontFamily: '"Playfair Display", serif', fontSize: '28px', color: '#2C2C2C', margin: '10px 0 5px 0' };
const nowPlayingAuthorStyle = { fontSize: '14px', color: '#888' };
const trackListStyle = { borderRadius: '16px', overflow: 'hidden' };
const trackListItemStyle = { display: 'flex', alignItems: 'center', padding: '14px 20px', fontSize: '15px', borderBottom: '1px solid #F5F5F5', transition: 'background 0.2s' };
const backButtonStyle = { backgroundColor: '#F0F0F0', border: 'none', color: '#333', cursor: 'pointer', fontSize: '15px', marginBottom: '20px', padding: '10px 20px', borderRadius: '50px', fontWeight: '500' };

// FOOTER - compact, with InsideArts logo
const footerStyle = { width: '100%', marginTop: '40px', paddingTop: '30px', borderTop: '1px solid #F0F0F0', display: 'flex', justifyContent: 'center' };
const footerContentStyle = { maxWidth: '800px', width: '100%', textAlign: 'center' };
const footerCollabStyle = { fontSize: '13px', color: '#888', letterSpacing: '1px', marginBottom: '0', fontWeight: '400' };
const footerLogoStyle = { height: '200px', width: 'auto', objectFit: 'contain', margin: '-60px auto -50px auto', display: 'block' };
const institutionalSectionStyle = { paddingTop: '20px', borderTop: '1px solid #F0F0F0', textAlign: 'center' };
const institutionalLogosStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '40px', marginBottom: '15px' };
const institutionalLogoStyle = { height: '60px', width: 'auto', objectFit: 'contain' };
const institutionalTextStyle = { fontSize: '12px', color: '#888', lineHeight: '1.5', maxWidth: '500px', margin: '0 auto', fontStyle: 'italic' };

export default App;